import classNames from "classnames";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BoardAiApi, BoardAiJqlResult } from "../../../services/board-ai-api";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import AiSparkleIcon from "./ai-sparkle-icon";
import { JqlValueDomains, parseJql, validateJqlSemantics } from "./jql";
import { JqlSuggestContext, JqlSuggestion, JqlSuggestionKind, JqlSuggestResult, suggestJql } from "./jql-suggest";
import { useBoardAiEnabled } from "./use-board-ai";

interface JqlBarProps {
  // Committed JQL from the URL — flows back in when a saved view or back/forward navigation
  // changes it externally. Our own commits also round-trip through here.
  initialValue: string;
  // Called with a debounced, *valid* (or empty) expression. Invalid partials never fire it,
  // so the column queries never refetch on a half-typed clause.
  onCommit: (jql: string) => void;
  context: JqlSuggestContext;
  // True while the board is actually fetching results for the committed query (the working-set
  // sync, or a server-fallback column query). Drives the in-field spinner alongside the local
  // "applying" window so the user always sees that the filter is being processed.
  busy?: boolean;
}

const DEBOUNCE_MS: number = 300;
const BLUR_CLOSE_MS: number = 120;

const KIND_LABEL: Record<JqlSuggestionKind, string> = {
  field: "field",
  operator: "op",
  value: "value",
  keyword: "logic"
};

export default function JqlBar({ initialValue, onCommit, context, busy = false }: JqlBarProps): JSX.Element {
  const [text, setText] = useState<string>(initialValue);
  const [caret, setCaret] = useState<number>(initialValue.length);
  const [open, setOpen] = useState<boolean>(false);
  const [active, setActive] = useState<number>(0);
  const aiEnabled: boolean = useBoardAiEnabled();
  // Natural-language → JQL lives in its OWN prompt popover (so plain English is never typed into
  // the JQL field, which only accepts JQL). nlOpen toggles it; nlText is the question.
  const [nlOpen, setNlOpen] = useState<boolean>(false);
  const [nlText, setNlText] = useState<string>("");
  const [aiBusy, setAiBusy] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const nlInputRef = useRef<HTMLInputElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const activeItemRef = useRef<HTMLLIElement | null>(null);
  // Last value we pushed to the parent — lets us tell our own commits apart from external
  // (saved-view / history) changes arriving via initialValue.
  const committedRef = useRef<string>(initialValue);
  // Caret position to restore after an accept re-renders the input.
  const pendingCaretRef = useRef<number | null>(null);

  // External URL changes (saved views, back/forward) flow in via initialValue.
  useEffect(() => {
    if (initialValue !== committedRef.current) {
      committedRef.current = initialValue;
      setText(initialValue);
      setCaret(initialValue.length);
    }
  }, [initialValue]);

  // Closed-domain value sets used to reject semantically-invalid clauses (e.g. priority = me)
  // before they're applied. Open-domain fields (assignee, labels, dates, …) accept any value.
  const domains: JqlValueDomains = useMemo(
    () => ({
      priority: new Set(context.priorities.map((value: string) => value.toLowerCase())),
      type: new Set(context.types.map((value: string) => value.toLowerCase())),
      status: new Set(context.statuses.flatMap((status) => [status.id.toLowerCase(), status.name.toLowerCase()])),
      project: new Set(context.projects.map((value: string) => value.toLowerCase()))
    }),
    [context]
  );

  // Debounced, validity-gated commit: only a syntactically AND semantically valid (or empty)
  // expression reaches the board, so a half-typed or unknown-value clause never applies or fires
  // a server-fallback request.
  useEffect(() => {
    const trimmed: string = text.trim();
    if (trimmed === committedRef.current.trim()) return;
    const handle: number = window.setTimeout(() => {
      if (!trimmed) {
        committedRef.current = "";
        onCommit("");
        return;
      }
      const clause = parseJql(trimmed);
      if (clause && validateJqlSemantics(clause, domains) === null) {
        committedRef.current = trimmed;
        onCommit(trimmed);
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [text, onCommit, domains]);

  const parseError: boolean = useMemo(() => text.trim().length > 0 && parseJql(text) === null, [text]);

  // Semantic error (parses, but an unknown value for a closed-domain field). null when the text
  // is empty, syntactically invalid (parseError owns that), or fully valid.
  const semanticError: string | null = useMemo(() => {
    const trimmed: string = text.trim();
    if (!trimmed) return null;
    const clause = parseJql(trimmed);
    if (!clause) return null;
    return validateJqlSemantics(clause, domains);
  }, [text, domains]);

  // A query is "in flight" from the user's perspective from the moment they type a valid change
  // until it's been committed AND the board finished loading its results. `pending` is the
  // debounce/commit window (text not yet pushed to the URL); `busy` is the actual board fetch.
  // A syntactically or semantically invalid clause shows the error, not a spinner — it will
  // never apply, so pretending it's loading would be a lie.
  const invalid: boolean = parseError || semanticError !== null;
  const pending: boolean = text.trim() !== initialValue.trim();
  const showSpinner: boolean = (pending && !invalid) || busy;

  const result: JqlSuggestResult = useMemo(() => suggestJql(text, caret, context), [text, caret, context]);
  const suggestions: JqlSuggestion[] = result.suggestions;

  // Reset the highlighted row whenever the suggestion list changes.
  useEffect(() => {
    setActive(0);
  }, [text, caret]);

  // Restore the caret after an accepted suggestion rewrites the value.
  useLayoutEffect(() => {
    if (pendingCaretRef.current !== null && inputRef.current) {
      const pos: number = pendingCaretRef.current;
      inputRef.current.setSelectionRange(pos, pos);
      pendingCaretRef.current = null;
    }
  });

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const accept = useCallback(
    (suggestion: JqlSuggestion): void => {
      const next: string = text.slice(0, result.replaceStart) + suggestion.insert + text.slice(result.replaceEnd);
      const pos: number = result.replaceStart + suggestion.insert.length;
      pendingCaretRef.current = pos;
      setText(next);
      setCaret(pos);
      setOpen(true);
    },
    [text, result]
  );

  const syncCaret = (el: HTMLInputElement): void => {
    setCaret(el.selectionStart ?? el.value.length);
  };

  // Turn the plain-English prompt into JQL and write it into the bar. The generated value flows
  // through the same debounce/validity gate as typed input, so an invalid generation never applies.
  const generateFromNl = (override?: string): void => {
    const question: string = (override ?? nlText).trim();
    if (!question || aiBusy) return;
    if (override !== undefined) {
      setNlText(override);
    }
    setAiBusy(true);
    void BoardAiApi.toJql(question)
      .then((result: BoardAiJqlResult): void => {
        pendingCaretRef.current = result.jql.length;
        setText(result.jql);
        setCaret(result.jql.length);
        setOpen(false);
        setNlOpen(false);
        setNlText("");
      })
      .catch((): void => undefined)
      .finally((): void => setAiBusy(false));
  };

  // Focus the prompt when the popover opens; close it on outside-click or Escape.
  useEffect((): (() => void) | void => {
    if (!nlOpen) return undefined;
    window.setTimeout((): void => nlInputRef.current?.focus(), 0);
    const onMouseDown = (event: MouseEvent): void => {
      if (!barRef.current?.contains(event.target as Node)) {
        setNlOpen(false);
      }
    };
    window.addEventListener("mousedown", onMouseDown);
    return (): void => window.removeEventListener("mousedown", onMouseDown);
  }, [nlOpen]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!open || suggestions.length === 0) {
      if (event.key === "ArrowDown") {
        setOpen(true);
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index: number) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index: number) => (index - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter" || event.key === "Tab") {
      const chosen: JqlSuggestion | undefined = suggestions[active];
      if (chosen) {
        event.preventDefault();
        accept(chosen);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="kuika-board__jql" aria-busy={showSpinner} ref={barRef}>
      {aiEnabled && (
        <button
          type="button"
          className={`kuika-board__jql-ai-btn${nlOpen ? " is-open" : ""}`}
          onClick={() => setNlOpen((prev: boolean) => !prev)}
          aria-label="Build a filter with AI"
          aria-expanded={nlOpen}
          title="Build a filter from a plain-English description"
        >
          <AiSparkleIcon size={13} className={aiBusy ? "kuika-board__ai-spin" : undefined} />
          <span>AI</span>
        </button>
      )}
      {showSpinner ? (
        <span className="kuika-board__jql-spinner" role="status" aria-label="Applying filter" />
      ) : (
        <KuikaSvg name="search" width={12} height={12} />
      )}
      <div className="kuika-board__jql-field">
        <input
          ref={inputRef}
          className="kuika-board__jql-input"
          value={text}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setText(event.target.value);
            setCaret(event.target.selectionStart ?? event.target.value.length);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => syncCaret(event.currentTarget)}
          onClick={(event: React.MouseEvent<HTMLInputElement>) => {
            syncCaret(event.currentTarget);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), BLUR_CLOSE_MS)}
          placeholder="JQL — e.g. priority = high AND updated >= -7d AND assignee = currentUser()"
          aria-label="JQL filter"
          spellCheck={false}
          autoComplete="off"
        />
        {open && suggestions.length > 0 && (
          <ul className="kuika-board__jql-suggest" role="listbox">
            {suggestions.map((suggestion: JqlSuggestion, index: number) => (
              <li
                key={`${suggestion.kind}-${suggestion.insert}-${index}`}
                ref={index === active ? activeItemRef : undefined}
                role="option"
                aria-selected={index === active}
                className={classNames("kuika-board__jql-suggest-item", { "is-active": index === active })}
                onMouseDown={(event: React.MouseEvent<HTMLLIElement>) => {
                  event.preventDefault();
                  accept(suggestion);
                }}
                onMouseEnter={() => setActive(index)}
              >
                <span className={`kuika-board__jql-suggest-kind kind-${suggestion.kind}`}>
                  {KIND_LABEL[suggestion.kind]}
                </span>
                <span className="kuika-board__jql-suggest-label">{suggestion.label}</span>
                {suggestion.hint && <span className="kuika-board__jql-suggest-hint">{suggestion.hint}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      {invalid && <span className="kuika-board__jql-error">{parseError ? "Invalid expression" : semanticError}</span>}
      {text && (
        <button
          type="button"
          className="kuika-board__jql-clear"
          onClick={() => {
            setText("");
            setCaret(0);
            inputRef.current?.focus();
          }}
          aria-label="Clear JQL"
        >
          ×
        </button>
      )}

      {nlOpen && (
        <div className="kuika-board__jql-ai-pop" role="dialog" aria-label="Build a filter with AI">
          <div className="kuika-board__jql-ai-pop-title">
            <AiSparkleIcon size={13} />
            Describe the issues you want to see
          </div>
          <div className="kuika-board__jql-ai-pop-row">
            <input
              ref={nlInputRef}
              className="kuika-board__jql-ai-pop-input"
              value={nlText}
              placeholder="e.g. urgent bugs assigned to me, updated this week"
              disabled={aiBusy}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNlText(event.target.value)}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  generateFromNl();
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  setNlOpen(false);
                }
              }}
            />
            <button
              type="button"
              className="kuika-board__jql-ai-pop-go"
              onClick={() => generateFromNl()}
              disabled={!nlText.trim() || aiBusy}
            >
              {aiBusy ? "Building…" : "Build filter"}
            </button>
          </div>
          <div className="kuika-board__jql-ai-pop-examples">
            {["urgent bugs assigned to me", "everything updated this week", "unassigned issues in progress"].map(
              (example: string) => (
                <button
                  key={example}
                  type="button"
                  className="kuika-board__jql-ai-pop-chip"
                  onClick={() => generateFromNl(example)}
                  disabled={aiBusy}
                >
                  {example}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
